import React, { useState, useMemo } from "react";
import { Dropdown, Button, Space, Tag, Row, Col } from "antd";
import type { MenuProps } from "antd";
import { FileExcelOutlined, DownOutlined, LeftOutlined, RightOutlined } from "@ant-design/icons";
import { COLORS, DirectoryItem } from "../types/types";

interface ExportButtonsProps {
  users: DirectoryItem[];
  exportProfessions: any[];
  availableCities: any[];
  cityStats: Record<string, number>;
  currentUserId: number | null;
  onExportProfession: (profession: any, amount: number) => void;
  onExportCity: (city: any) => void;
  exporting?: boolean;
}

// Constantes pour la limitation
const CITIES_PER_PAGE = 50;
const MAX_PROFESSIONS_DISPLAY = 50;

const ExportButtons: React.FC<ExportButtonsProps> = ({
  users,
  exportProfessions,
  availableCities,
  cityStats,
  currentUserId,
  onExportProfession,
  onExportCity,
  exporting = false,
}) => {
  const [exportDropdownVisible, setExportDropdownVisible] = useState(false);
  const [cityDropdownVisible, setCityDropdownVisible] = useState(false);
  const [selectedExportProfession, setSelectedExportProfession] = useState<any>(null);
  const [selectedCity, setSelectedCity] = useState<any>(null);
  
  // État pour la pagination des villes
  const [cityPage, setCityPage] = useState(1);

  // Optimisation 1 : Mémoriser le comptage des utilisateurs par profession
  const userCountByProfession = useMemo(() => {
    return users.reduce((acc, user) => {
      if (user.show_in_directory && user.profession) {
        acc[user.profession] = (acc[user.profession] || 0) + 1;
      }
      return acc;
    }, {} as Record<string, number>);
  }, [users]);

  // Optimisation 2 : Filtrer les professions avec utilisateurs
  const exportProfessionMenuItems: MenuProps['items'] = useMemo(() => {
    if (!exportProfessions.length) {
      return [{ key: 'loading', label: 'Chargement...', disabled: true }];
    }

    // Ne garder que les professions qui ont des utilisateurs
    const professionsWithUsers = exportProfessions.filter(
      p => userCountByProfession[p.name] > 0
    );

    if (professionsWithUsers.length === 0) {
      return [{ key: 'no-users', label: 'Aucune profession avec des utilisateurs', disabled: true }];
    }

    const freeProfessions = professionsWithUsers.filter(p => p.isFree);
    const paidProfessions = professionsWithUsers.filter(p => !p.isFree);

    const items: MenuProps['items'] = [];

    // Limiter l'affichage à 50 professions max
    if (freeProfessions.length > 0) {
      const displayFree = freeProfessions.slice(0, MAX_PROFESSIONS_DISPLAY);
      items.push({
        type: 'group',
        label: `Gratuit (${freeProfessions.length})`,
        children: displayFree.map(prof => ({
          key: `free-${prof.id}`,
          label: (
            <div style={{ display: "flex", justifyContent: "space-between", width: "100%", padding: "8px 12px" }}>
              <Space>
                <span>{prof.name}</span>
                <Tag color="blue">{userCountByProfession[prof.name]}</Tag>
              </Space>
              <Tag color={COLORS.primary}>Gratuit</Tag>
            </div>
          ),
          onClick: () => {
            setSelectedExportProfession(prof);
            setExportDropdownVisible(false);
            onExportProfession(prof, 0);
          }
        }))
      });

      // Ajouter un message si plus de professions
      if (freeProfessions.length > MAX_PROFESSIONS_DISPLAY) {
        items.push({
          key: 'free-more',
          label: <div style={{ textAlign: 'center', color: COLORS.textLight, padding: 8 }}>
            + {freeProfessions.length - MAX_PROFESSIONS_DISPLAY} autres professions gratuites
          </div>,
          disabled: true
        });
      }
    }

    if (paidProfessions.length > 0) {
      const displayPaid = paidProfessions.slice(0, MAX_PROFESSIONS_DISPLAY);
      items.push({
        type: 'group',
        label: `Payant (${paidProfessions.length})`,
        children: displayPaid.map(prof => ({
          key: `paid-${prof.id}`,
          label: (
            <div style={{ display: "flex", justifyContent: "space-between", width: "100%", padding: "8px 12px" }}>
              <Space>
                <span>{prof.name}</span>
                <Tag color="blue">{userCountByProfession[prof.name]}</Tag>
              </Space>
              <span style={{ fontWeight: 600, color: COLORS.secondary }}>{prof.exportPrice} €</span>
            </div>
          ),
          onClick: () => {
            setSelectedExportProfession(prof);
            setExportDropdownVisible(false);
            onExportProfession(prof, prof.exportPrice);
          }
        }))
      });

      // Ajouter un message si plus de professions
      if (paidProfessions.length > MAX_PROFESSIONS_DISPLAY) {
        items.push({
          key: 'paid-more',
          label: <div style={{ textAlign: 'center', color: COLORS.textLight, padding: 8 }}>
            + {paidProfessions.length - MAX_PROFESSIONS_DISPLAY} autres professions payantes
          </div>,
          disabled: true
        });
      }
    }

    return items;
  }, [exportProfessions, userCountByProfession, onExportProfession]);

  // Optimisation 3 : Filtrer et paginer les villes
  const cityExportMenuItems: MenuProps['items'] = useMemo(() => {
    if (!availableCities.length) {
      return [{ key: 'loading', label: 'Chargement...', disabled: true }];
    }

    // 1. Filtrer les villes qui ont des contacts
    const citiesWithContacts = availableCities
      .filter(city => (cityStats[city.name] || 0) > 0)
      .sort((a, b) => a.name.localeCompare(b.name));

    if (citiesWithContacts.length === 0) {
      return [{ key: 'no-cities', label: 'Aucune ville avec des contacts', disabled: true }];
    }

    // 2. Calculer la pagination
    const totalCities = citiesWithContacts.length;
    const totalPages = Math.ceil(totalCities / CITIES_PER_PAGE);
    const startIndex = (cityPage - 1) * CITIES_PER_PAGE;
    const endIndex = Math.min(startIndex + CITIES_PER_PAGE, totalCities);
    
    // 3. Prendre les villes de la page courante
    const currentPageCities = citiesWithContacts.slice(startIndex, endIndex);
    
    // 4. Construire les items
    const items: MenuProps['items'] = [];

    // Ajouter les villes de la page courante
    currentPageCities.forEach(city => {
      const contactCount = cityStats[city.name] || 0;
      items.push({
        key: `city-${city.name}`,
        label: (
          <div style={{ display: "flex", justifyContent: "space-between", width: "100%", padding: "8px 12px" }}>
            <Space>
              <span role="img" aria-label="city" style={{ fontSize: "16px" }}>🏙️</span>
              <span style={{ fontWeight: 500 }}>{city.name}</span>
              <Tag color="blue">{contactCount}</Tag>
            </Space>
          </div>
        ),
        onClick: () => {
          setSelectedCity(city);
          setCityDropdownVisible(false);
          setCityPage(1); // Reset à la page 1 après sélection
          onExportCity(city);
        }
      });
    });

    // 5. Ajouter les contrôles de pagination
    if (totalPages > 1) {
      // Séparateur
      items.push({
        key: 'pagination-divider',
        type: 'divider',
      });
      
      // Contrôles de pagination
      items.push({
        key: 'pagination',
        label: (
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            padding: '8px 12px',
            backgroundColor: COLORS.light,
            borderRadius: '4px'
          }}>
            <Button
              type="text"
              size="small"
              icon={<LeftOutlined />}
              disabled={cityPage === 1}
              onClick={(e) => {
                e.stopPropagation();
                setCityPage(prev => Math.max(1, prev - 1));
              }}
              style={{ color: cityPage === 1 ? COLORS.textLight : COLORS.primary }}
            />
            
            <span style={{ fontSize: '12px', color: COLORS.text }}>
              Page {cityPage} / {totalPages}
              <span style={{ marginLeft: '8px', color: COLORS.textLight }}>
                ({totalCities} villes)
              </span>
            </span>
            
            <Button
              type="text"
              size="small"
              icon={<RightOutlined />}
              disabled={cityPage === totalPages}
              onClick={(e) => {
                e.stopPropagation();
                setCityPage(prev => Math.min(totalPages, prev + 1));
              }}
              style={{ color: cityPage === totalPages ? COLORS.textLight : COLORS.primary }}
            />
          </div>
        ),
        onClick: (e) => {
          e.domEvent.stopPropagation(); // Empêcher la fermeture du dropdown
        }
      });
    }

    return items;
  }, [availableCities, cityStats, onExportCity, cityPage]);

  return (
    <Row gutter={[24, 16]} style={{ marginBottom: 24 }}>
      <Col xs={24} style={{ display: "flex", justifyContent: "flex-end", gap: 8, flexWrap: "wrap" }}>
        
        {/* Export par profession */}
        <Dropdown
          menu={{ 
            items: exportProfessionMenuItems, 
            style: { maxHeight: 400, overflowY: "auto", minWidth: 300 } 
          }}
          trigger={['click']}
          onOpenChange={setExportDropdownVisible}
          open={exportDropdownVisible}
          disabled={exporting || !exportProfessions.length}
        >
          <Button
            loading={exporting}
            style={{
              background: exporting ? "#ccc" : (selectedExportProfession?.isFree ? COLORS.primary : COLORS.secondary),
              borderColor: exporting ? "#ccc" : (selectedExportProfession?.isFree ? COLORS.primary : COLORS.secondary),
              color: "white",
              minWidth: 280,
              height: 44,
              display: "flex",  
              alignItems: "center",
              justifyContent: "space-between"
            }}
            disabled={exporting || !exportProfessions.length}
          >
            <Space>
              <FileExcelOutlined />
              <span style ={{color:"white", fontWeight: "bold"}}>Exporter par profession</span>
            </Space>
            <DownOutlined />
          </Button>
        </Dropdown>

        {/* Export par ville avec pagination */}
        <Dropdown
          menu={{ 
            items: cityExportMenuItems, 
            style: { maxHeight: 500, overflowY: "auto", minWidth: 350 } 
          }}
          trigger={['click']}
          onOpenChange={(open) => {
            setCityDropdownVisible(open);
            if (open) setCityPage(1); // Reset à la page 1 à l'ouverture
          }}
          open={cityDropdownVisible}
          disabled={exporting || !availableCities.length}
        >
          <Button
            loading={exporting}
            style={{
              background: exporting ? "#ccc" : "#4bb0c0",
              borderColor: exporting ? "#ccc" : "#4bb0c0",
              color: "white",
              minWidth: 280,
              height: 44,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between"
            }}
            disabled={exporting || !availableCities.length}
          >
            <Space>
              <span style ={{textAlign:"center",justifyContent:"center",color:"white", fontWeight: "bold"}}> Exporter par ville</span>
            </Space>
            <DownOutlined />
          </Button>
        </Dropdown>
      </Col>
    </Row>
  );
};

export default React.memo(ExportButtons);